import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { getQuizResults } from '../../services/quizService';
import BackLink from '../../components/ui/BackLink';
import { toast } from 'sonner';

const QuizResultsPage = () => {
  const { session_id: sessionId } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const data = await getQuizResults(sessionId);
        setResults(data);
      } catch (error) {
        toast.error('Failed to load quiz results');
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [sessionId]);

  const calculateStats = (results) => {
    const total = results.length;
    const correct = results.filter(result => result.is_correct).length;
    return {
      total,
      correct,
      percentage: Math.round((correct / total) * 100)
    };
  };

  if (loading) {
    return <div className="text-center py-4">Loading results...</div>;
  }

  if (!results) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-5">
        <h2 className="text-2xl font-bold mb-4">Results Not Found</h2>
        <p className="text-gray-600 mb-6">We couldn't find the quiz results for this session. Please try again later or contact support if the issue persists.</p>
        <BackLink />
      </div>
    );
  }

  const stats = calculateStats(results);

  return (
    <div className="max-w-4xl mx-auto p-4">
      <BackLink />
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-6">Quiz Results</h1>
        <div className="stats shadow">
          <div className="stat">
            <div className="stat-title">Score</div>
            <div className={`stat-value ${stats.percentage >= 50 ? 'text-success' : 'text-error'}`}>
              {stats.percentage}%
            </div>
          </div>
          <div className="stat">
            <div className="stat-title">Correct Answers</div>
            <div className="stat-value text-primary">{stats.correct}/{stats.total}</div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {results.map((result, index) => {
          const options = result.options || [];
          const selectedOption = result.user_answer?.selected_option;
          const selectedOptions = Array.isArray(selectedOption) ? selectedOption : selectedOption ? [selectedOption] : [];
          const selectedOptionIds = selectedOptions.map(opt => typeof opt === 'object' ? opt.id : opt);
          const correctAnswer = result.correct_answer;
          const isCorrect = result.is_correct;

          return (
            <div key={result.id} className="card bg-base-100 shadow-lg">
              <div className="card-body">
                <div className="flex justify-between items-start">
                  <h3 className="card-title flex-1">
                    Question {index + 1}
                    <span className={`badge ${isCorrect ? 'badge-success' : 'badge-error'} ml-2`}>
                      {isCorrect ? 'Correct' : 'Incorrect'}
                    </span>
                  </h3>
                  <span className="badge badge-outline">{result.question_type}</span>
                </div>
                <p className="text-lg my-4">{result.text}</p>
                {result.image && (
                  <img src={result.image} alt="Question" className="max-h-40 mb-4 rounded w-fit mx-auto" />
                )}

                {/* OBJECTIVE */}
                {result.question_type === 'objective' && (
                  <div className="space-y-2">
                    {options.map((option) => {
                      const isSelected = selectedOption && option.id === selectedOption.id;
                      const showExplanation = isSelected && option.explanation;
                      const explanationColor = result.is_correct
                        ? "bg-green-50 border-green-200 text-green-800"
                        : "bg-red-50 border-red-200 text-red-800";
                      return (
                        <div
                          key={option.id}
                          className={`p-3 rounded-lg border ${
                            option.is_correct ? 'bg-success/10 border-success' :
                            isSelected ? 'bg-error/10 border-error' :
                            'border-gray-200'
                          }`}
                        >
                          <div className="flex items-center">
                            <span className="flex-1">{option.text}</span>
                            {option.is_correct && <span className="text-success">✓</span>}
                            {!option.is_correct && isSelected && <span className="text-error">✗</span>}
                          </div>
                          {showExplanation && (
                            <div className={`mt-2 p-2 rounded border ${explanationColor}`}>
                              <strong>Explanation:</strong> {option.explanation}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* MULTIPLE SELECT */}
                {result.question_type === 'multiple_select' && (
                  <div className="space-y-2">
                    {options.map((option) => {
                      const isSelected = (result.user_answer?.selected_options || []).some(
                        (opt) => opt.id === option.id
                      );
                      const showExplanation = isSelected && option.explanation;
                      const explanationColor = result.is_correct
                        ? "bg-green-50 border-green-200 text-green-800"
                        : "bg-red-50 border-red-200 text-red-800";
                      return (
                        <div
                          key={option.id}
                          className={`p-3 rounded-lg border flex flex-col ${
                            option.is_correct
                              ? 'bg-success/10 border-success'
                              : isSelected
                              ? 'bg-error/10 border-error'
                              : 'border-gray-200'
                          }`}
                        >
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              className="checkbox checkbox-sm mr-2"
                            />
                            <span className="flex-1">{option.text}</span>
                            {option.is_correct && <span className="text-success ml-2">✓</span>}
                            {!option.is_correct && isSelected && (
                              <span className="text-error ml-2">✗</span>
                            )}
                          </div>
                          {showExplanation && (
                            <div className={`mt-2 p-2 rounded border ${explanationColor}`}>
                              <strong>Explanation:</strong> {option.explanation}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* THEORY */}
                {result.question_type === 'theory' && (
                  <div className="space-y-2">
                    <div className="p-3 rounded-lg border border-gray-200">
                      <strong>Your Answer:</strong>
                      <div className="mt-1">{result.user_answer?.theory_answer || <span className="italic text-gray-400">No answer</span>}</div>
                    </div>
                    <div className="p-3 rounded-lg border border-success bg-success/10">
                      <strong>Correct Answer:</strong>
                      <div className="mt-1">{result.correct_answer}</div>
                    </div>
                  </div>
                )}

                {/* MATCHING */}
                {result.question_type === 'matching' && (
                  <div className="space-y-3">
                    <div className="overflow-x-auto">
                      <table className="table w-full">
                        <thead>
                          <tr>
                            <th className="bg-base-200">Item</th>
                            <th className="bg-base-200">Your Match</th>
                            <th className="bg-base-200">Correct Match</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(result.matching_pair || []).map((pair) => {
                            const userMatch = (result.user_answer?.matching_answers || []).find(
                              ans => ans.matching_pair === pair.id
                            );
                            const isCorrect = userMatch && userMatch.selected_right === pair.right;
                            return (
                              <tr key={pair.id} className={isCorrect ? "bg-success/10" : "bg-error/10"}>
                                <td className="font-medium">{pair.left}</td>
                                <td>
                                  {userMatch ? (
                                    <span className={isCorrect ? "text-success" : "text-error"}>
                                      {userMatch.selected_right}
                                      {isCorrect ? " ✓" : " ✗"}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">No answer</span>
                                  )}
                                </td>
                                <td>
                                  <span className="text-success">{pair.right}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuizResultsPage;
